// lib/services/capture/camera_dump.ts
//
// Pure, dependency-free diagnostic serializer + camera dump builder. Used when
// the user EXPLICITLY selects a camera in the Camera Selector, to emit a
// complete, human-readable snapshot of everything the browser exposed about that
// camera to the in-app console — so two same-facing lenses can be compared field
// by field.
//
// Design goals:
//   - Robust: never throws on circular refs, bigint, symbols, functions,
//     Errors, DOM-like objects, non-finite numbers, or throwing getters.
//   - Faithful: preserves unknown / non-standard fields dynamically rather than
//     whitelisting only the standard WebRTC keys, so browser-specific data is
//     visible.
//   - Side-effect-free: never invokes arbitrary accessors. Own accessor
//     properties are recorded by descriptor (kind/enumerable/getter presence),
//     never called. Only the standard, explicitly-requested MediaDeviceInfo
//     toJSON() method is invoked (guarded).
//
// This module contains NO camera acquisition, NO getUserMedia, and NO focus
// handling. It only formats data the caller already captured during discovery.
// If the browser happens to expose focus-related fields inside a raw snapshot,
// they are preserved verbatim like any other unknown field — but nothing here
// requests, inspects for control logic, or sets any focus parameter.

const MAX_DEPTH = 8;

function err_name(e: unknown): string {
  return e instanceof Error ? `${e.name}: ${e.message}` : String(e);
}

// Convert an arbitrary value into a JSON-safe structure, replacing anything that
// JSON.stringify cannot represent (or that would be unsafe to touch) with a
// descriptive string token. Circular references are broken with "<circular>".
function sanitize(value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (value === null) return null;

  const t = typeof value;
  if (t === 'undefined') return '<undefined>';
  if (t === 'bigint') return `<bigint ${(value as bigint).toString()}n>`;
  if (t === 'symbol') return `<symbol ${String(value as symbol)}>`;
  if (t === 'function') {
    const fn = value as (...args: unknown[]) => unknown;
    return `<function ${fn.name || 'anonymous'}>`;
  }
  if (t === 'number') {
    return Number.isFinite(value as number) ? (value as number) : `<number ${String(value)}>`;
  }
  if (t === 'boolean' || t === 'string') return value;

  // Objects from here on.
  const obj = value as object;

  if (seen.has(obj)) return '<circular>';

  if (obj instanceof Error) {
    return { __type: 'Error', name: obj.name, message: obj.message };
  }

  // DOM-like duck typing (Node/Element/Window etc.). We never traverse these —
  // they carry huge cyclic graphs and live host objects.
  const anyObj = obj as Record<string, unknown>;
  if (typeof anyObj.nodeType === 'number' && typeof anyObj.nodeName === 'string') {
    return `<DOM ${String(anyObj.nodeName)}>`;
  }

  if (depth >= MAX_DEPTH) return '<max depth>';

  seen.add(obj);
  try {
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        try {
          return sanitize(item, seen, depth + 1);
        } catch (e) {
          return `<throw ${err_name(e)}>`;
        }
      });
    }

    const out: Record<string, unknown> = {};
    let names: string[];
    try {
      names = Object.keys(obj); // own enumerable string keys only
    } catch {
      return '<unreadable object>';
    }

    for (const key of names) {
      let desc: PropertyDescriptor | undefined;
      try {
        desc = Object.getOwnPropertyDescriptor(obj, key);
      } catch {
        out[key] = '<descriptor error>';
        continue;
      }
      if (!desc) continue;

      // Accessor property: DO NOT invoke it (side effects / may throw). Record
      // its shape instead.
      if (!('value' in desc)) {
        out[key] = typeof desc.get === 'function' ? '<getter>' : '<setter-only>';
        continue;
      }

      try {
        out[key] = sanitize(desc.value, seen, depth + 1);
      } catch (e) {
        out[key] = `<throw ${err_name(e)}>`;
      }
    }
    return out;
  } finally {
    seen.delete(obj);
  }
}

// Circular-safe, side-effect-free pretty JSON. Never throws.
export function safe_stringify(value: unknown, indent = 2): string {
  try {
    const sanitized = sanitize(value, new WeakSet<object>(), 0);
    const json = JSON.stringify(sanitized, null, indent);
    return json === undefined ? String(sanitized) : json;
  } catch (e) {
    return `<unserializable: ${err_name(e)}>`;
  }
}

export interface PropertyMeta {
  kind: 'data' | 'accessor';
  enumerable: boolean;
  configurable: boolean;
  writable?: boolean;
  value_type?: string;
  has_getter?: boolean;
  has_setter?: boolean;
}

export interface ObjectMetadata {
  constructor_name: string | null;
  own_property_names: string[];
  own_property_descriptors: Record<string, PropertyMeta>;
  // Names defined on the immediate prototype (where MediaDeviceInfo exposes its
  // deviceId/kind/label/groupId/toJSON getters, which are non-own).
  prototype_property_names: string[];
}

// Inspect an object's own property names/descriptors and its prototype's
// property names WITHOUT invoking any accessor. This is how browser-specific and
// non-enumerable fields (e.g. MediaDeviceInfo's prototype getters) become
// visible. Returns null for non-objects.
export function describe_metadata(value: unknown): ObjectMetadata | null {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) {
    return null;
  }
  const obj = value as object;

  let own_property_names: string[] = [];
  try {
    own_property_names = Object.getOwnPropertyNames(obj);
  } catch {
    own_property_names = [];
  }

  const own_property_descriptors: Record<string, PropertyMeta> = {};
  for (const name of own_property_names) {
    try {
      const d = Object.getOwnPropertyDescriptor(obj, name);
      if (!d) continue;
      if ('value' in d) {
        own_property_descriptors[name] = {
          kind: 'data',
          enumerable: !!d.enumerable,
          configurable: !!d.configurable,
          writable: !!d.writable,
          value_type: typeof d.value, // reading a data value does not run a getter
        };
      } else {
        own_property_descriptors[name] = {
          kind: 'accessor',
          enumerable: !!d.enumerable,
          configurable: !!d.configurable,
          has_getter: typeof d.get === 'function',
          has_setter: typeof d.set === 'function',
        };
      }
    } catch {
      // Skip any property whose descriptor cannot be read.
    }
  }

  let prototype_property_names: string[] = [];
  try {
    const proto = Object.getPrototypeOf(obj);
    if (proto) prototype_property_names = Object.getOwnPropertyNames(proto);
  } catch {
    prototype_property_names = [];
  }

  let constructor_name: string | null = null;
  try {
    constructor_name = (obj as { constructor?: { name?: string } }).constructor?.name ?? null;
  } catch {
    constructor_name = null;
  }

  return {
    constructor_name,
    own_property_names,
    own_property_descriptors,
    prototype_property_names,
  };
}

export interface CameraDumpInput {
  friendly: string;
  index: number;
  facing: string;
  selection_source: string;
  device_id: string;
  group_id?: string | null;
  // Raw MediaDeviceInfo reference captured during discovery (may be null).
  media_device_info?: unknown;
  // Full, un-whitelisted snapshots captured off the probe track.
  raw_settings?: unknown;
  raw_capabilities?: unknown;
  raw_constraints?: unknown;
  // The engine's CameraInfo object/state and its whitelisted diagnostics.
  camera_info?: unknown;
  diagnostics?: unknown;
  // Device/browser model diagnostics already captured at discovery.
  device_model?: string | null;
  device_model_note?: string | null;
}

export const DUMP_BEGIN = '[camera dump begin]';
export const DUMP_END = '[camera dump end]';
const LINE = '[camera dump]';

// Build the full, delimited, pretty-printed dump as an array of console lines.
// Every section is serialized with the safe serializer, so this never throws on
// hostile input; the worst case is a section rendered as an error token.
export function build_camera_dump(input: CameraDumpInput): string[] {
  const header = `friendly="${input.friendly}" deviceId=${input.device_id}`;
  const out: string[] = [];
  out.push(`${DUMP_BEGIN} ${header}`);

  const section = (title: string, value: unknown): void => {
    out.push(`${LINE} --- ${title} ---`);
    for (const line of safe_stringify(value).split('\n')) {
      out.push(`${LINE}   ${line}`);
    }
  };

  section('selection', {
    friendly: input.friendly,
    index: input.index,
    facing: input.facing,
    selection_source: input.selection_source,
    device_id: input.device_id,
    group_id: input.group_id ?? null,
  });

  const mdi = input.media_device_info ?? null;

  // MediaDeviceInfo.toJSON() — the standard, explicitly-requested serialization
  // hook. Invoked with a guard; everything else is descriptor-only.
  let to_json: unknown;
  if (mdi && typeof (mdi as { toJSON?: unknown }).toJSON === 'function') {
    try {
      to_json = (mdi as { toJSON: () => unknown }).toJSON();
    } catch (e) {
      to_json = `<toJSON threw: ${err_name(e)}>`;
    }
  } else {
    to_json = mdi ? '<toJSON not available>' : '<no MediaDeviceInfo captured>';
  }
  section('MediaDeviceInfo.toJSON()', to_json);
  section('MediaDeviceInfo metadata (own + prototype)', describe_metadata(mdi));
  section('MediaDeviceInfo raw enumerable fields', mdi ?? '<no MediaDeviceInfo captured>');

  section('track getSettings() (raw)', input.raw_settings ?? '<not captured>');
  section('track getCapabilities() (raw)', input.raw_capabilities ?? '<not captured>');
  section('track getConstraints() (raw)', input.raw_constraints ?? '<not captured>');

  section('CameraInfo object/state', input.camera_info ?? '<not captured>');
  section('diagnostics + wide-angle heuristic', input.diagnostics ?? '<not captured>');

  section('device/browser model', {
    device_model: input.device_model ?? null,
    device_model_note: input.device_model_note ?? null,
  });

  out.push(`${DUMP_END} ${header}`);
  return out;
}
